<template>
  <div class="planners-list">
    <!-- Header -->
    <template v-if="preview">
      <slot name="header" :planners="planners">
        <v-card-subtitle class="d-flex pt-2">
          <p class="heading text-h6 mb-0">{{ $t("t_planners") }}</p>
          <v-spacer></v-spacer>
          <v-btn small class="mt-1" v-if="planners && planners.length" text :to="{ name: 'planners' }">
            {{ $t("t_view_all") }} <v-icon small class="ml-1">mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-subtitle>
      </slot>
    </template>

    <!-- No results message -->
    <h4 class="pa-4 pt-0" v-if="(planners?.length == 0 && !loading) || !$online">
      {{ $t("t_no_planners") }} {{ !$online ? " : " + $t("t_no_network_connection") : "" }}
    </h4>

    <div v-else>
      <template v-if="planners && Array.isArray(planners) && planners.length">
        <div
          v-for="(item, index) in planners"
          :key="`${item.plannerId}-${item.id}-${index}`"
          class="planner-card-wrapper mx-2 mb-3"
        >
          <v-card class="planner-card" :class="{ 'theme--dark': $vuetify.theme.dark }" flat>
            <!-- Planner Info Section -->
            <v-card-text class="planner-info pb-2 pt-3">
              <!-- Type and Status Row -->
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-overline grey--text" style="line-height: 1.2">
                  {{ item.plannerType ? $t("t_planner_type_" + item.plannerType) : "" }}
                </span>
                <div class="planner-status-badge">
                  <template v-if="item.selectedDueDate">
                    <div
                      class="status-badge-block"
                      :class="`status-badge--${getPlannerStatusColor(item.selectedDueDate.status)}`"
                    >
                      {{ getStatusText(item.selectedDueDate.status) }}
                      <template v-if="getCompletionPercentage(item)"> - {{ getCompletionPercentage(item) }}% </template>
                    </div>
                  </template>
                  <template v-else-if="item.currentSubmission">
                    <div
                      class="status-badge-block"
                      :class="`status-badge--${getPlannerStatusColor(item.currentSubmission.status || 0)}`"
                    >
                      {{ getStatusText(item.currentSubmission.status || 0) }}
                      <template v-if="item.currentSubmission.completionPercentage">
                        - {{ item.currentSubmission.completionPercentage }}%
                      </template>
                    </div>
                  </template>
                  <template v-else-if="isOpenEndedPlanner(item)">
                    <div class="status-badge-block status-badge--grey">{{ $t("t_ready") }}</div>
                  </template>
                  <template v-else>
                    <div class="status-badge-block" :class="`status-badge--${getPlannerStatusColor(item.status || 0)}`">
                      {{ getStatusText(item.status || 0) }}
                    </div>
                  </template>
                </div>
              </div>

              <!-- Planner Name -->
              <div class="text-subtitle-1 font-weight-medium planner-name">{{ item.plannerName }}</div>

              <!-- Due Date or Open Ended -->
              <div class="text-caption mt-1">
                <template v-if="item.selectedDueDate">
                  <v-menu offset-y v-model="item.selectDueDateOpen">
                    <template v-slot:activator="{ on, attrs }">
                      <span class="grey--text mr-1">{{ $t("t_due") }}:</span>
                      <v-btn @click.stop x-small text class="px-1 due-date-btn" v-bind="attrs" v-on="on">
                        {{ formatDateTime(item.selectedDueDate.dueDateTime) }}
                        <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item
                        v-for="(dueDate, dIndex) in getDueDatesLimited(item)"
                        :key="`${item.id}-${dueDate.id}-${dIndex}`"
                        @click.stop="selectDueDate(item, dueDate)"
                      >
                        <v-list-item-title>{{ formatDateTime(dueDate.dueDateTime) }}</v-list-item-title>
                      </v-list-item>
                      <template v-if="getTotalDueDates(item) > dueDateLimit">
                        <v-divider></v-divider>
                        <v-list-item @click.stop="showAllDueDates(item)">
                          <v-list-item-title class="text-center primary--text">
                            {{ $t("t_view_all") }} ({{ getTotalDueDates(item) }})
                          </v-list-item-title>
                        </v-list-item>
                      </template>
                    </v-list>
                  </v-menu>
                  <planner-due-dates-dialog
                    v-if="currentDialogPlanner && currentDialogPlanner.id === item.id"
                    :show="showDueDatesDialog"
                    :planner="item"
                    @close="closeDueDatesDialog"
                    @select-date="navigateToPlannerWithDate"
                  />
                </template>
                <span v-else class="grey--text">{{ $t("t_open_ended") }}</span>
              </div>
            </v-card-text>

            <!-- Action Buttons Row -->
            <div class="planner-actions" v-if="hasAnyAction(item)">
              <v-btn
                v-if="canStartOrResume(item)"
                text
                tile
                class="planner-action-btn"
                :class="{ 'flex-grow-1': getActionButtonCount(item) === 1 }"
                @click="handleStartOrResume(item)"
              >
                <v-icon small left :color="hasInProgress(item) ? 'primary' : 'success'">
                  {{ hasInProgress(item) ? "mdi-play" : "mdi-plus" }}
                </v-icon>
                {{ hasInProgress(item) ? $t("t_continue") : $t("t_start") }}
              </v-btn>

              <v-btn
                v-if="hasInProgress(item) && (isOpenEndedPlanner(item) || hasMultipleInProgress(item))"
                text
                tile
                class="planner-action-btn"
                :class="{ 'flex-grow-1': getActionButtonCount(item) === 1 }"
                @click="openActionMenu(item, 'active')"
              >
                <v-icon small left color="primary">mdi-progress-clock</v-icon>
                {{ $t("t_active") }}
              </v-btn>

              <v-btn
                v-if="hasHistory(item)"
                text
                tile
                class="planner-action-btn"
                :class="{ 'flex-grow-1': getActionButtonCount(item) === 1 }"
                @click="openActionMenu(item, 'history')"
              >
                <v-icon small left color="grey">mdi-history</v-icon>
                {{ $t("t_history") }}
                <span v-if="item.submittedSubmissionsCount" class="ml-1">({{ item.submittedSubmissionsCount }})</span>
              </v-btn>
            </div>
          </v-card>
        </div>
      </template>

      <!-- Loading -->
      <v-row v-if="loading">
        <v-col class="d-flex flex-column align-center justify-center mx-3 mt-2 mb-0">
          <v-progress-linear indeterminate color="primary" size="32"></v-progress-linear>
        </v-col>
      </v-row>

      <!-- Load More -->
      <v-row>
        <v-col>
          <v-btn
            v-if="!endOfResults && !loading && planners && planners.length > 0"
            class="mx-3"
            @click="loadMorePlanners"
          >
            {{ $t("t_load_more") }}
          </v-btn>
        </v-col>
        <v-col class="text-right" v-if="!preview">
          <div class="text-caption grey--text mx-3 mb-2 mt-2">
            {{ $t("t_showing") }} {{ planners.length }} {{ $t("t_of") }} {{ totalItems }}
          </div>
        </v-col>
      </v-row>

      <!-- Action Menu (Bottom Sheet) -->
      <planner-action-menu
        v-if="actionMenuPlanner"
        :show="showActionMenuSheet"
        :planner="actionMenuPlanner"
        :mode="actionMenuMode"
        @close="closeActionMenu"
        @start-new="startNewPlanner"
        @select-submission="navigateToPlannerSubmission"
      />
    </div>
  </div>
</template>

<script>
import { PlannerService } from "@services";
import helpers from "@/mixins/helpers";
import { DateTime } from "luxon";
import PlannerDueDatesDialog from "./PlannerDueDatesDialog.vue";
import PlannerActionMenu from "./PlannerActionMenu.vue";
import statusBadgeMixin from "@/mixins/statusBadge";

export default {
  name: "PlannerList",
  components: {
    PlannerDueDatesDialog,
    PlannerActionMenu,
  },

  mixins: [helpers, statusBadgeMixin],

  props: {
    takeNum: {
      type: Number,
      default: 25,
    },
    showFilters: {
      type: Boolean,
      default: false,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: "",
    },
  },

  data: () => ({
    loading: false,
    planners: [],
    dueDateLimit: 4,
    skipNum: 0,
    totalItems: 0,
    endOfResults: false,
    currentDialogPlanner: null,
    showDueDatesDialog: false,
    actionMenuPlanner: null,
    showActionMenuSheet: false,
    actionMenuMode: "history", // 'active' or 'history'
  }),

  methods: {
    // === State Checks ===
    isOpenEndedPlanner(item) {
      return !item.selectedDueDate && (!item.dueDates || item.dueDates.length === 0);
    },

    hasInProgress(item) {
      return item.currentSubmission && (item.currentSubmission.status === 0 || item.currentSubmission.status === 1);
    },

    // eslint-disable-next-line no-unused-vars
    hasMultipleInProgress(_item) {
      return false;
    },

    hasHistory(item) {
      return item.submittedSubmissionsCount && item.submittedSubmissionsCount > 0;
    },

    canStartOrResume(item) {
      // Can start new if open-ended or has active submission to resume
      if (this.hasInProgress(item)) return true;
      if (this.isOpenEndedPlanner(item)) return true;
      // For due-date planners without submission, can start
      if (item.selectedDueDate && !item.selectedDueDate.submissionId) return true;
      return false;
    },

    hasAnyAction(item) {
      return this.canStartOrResume(item) || this.hasHistory(item);
    },

    getActionButtonCount(item) {
      let count = 0;
      if (this.canStartOrResume(item)) count++;
      if (this.hasInProgress(item) && (this.isOpenEndedPlanner(item) || this.hasMultipleInProgress(item))) count++;
      if (this.hasHistory(item)) count++;
      return count;
    },

    getCompletionPercentage(item) {
      if (
        item.currentSubmission?.dueDateId === item.selectedDueDate?.id &&
        item.currentSubmission?.completionPercentage
      ) {
        return item.currentSubmission.completionPercentage;
      }
      return item.selectedDueDate?.completionPercentage || null;
    },

    // === Actions ===
    handleStartOrResume(item) {
      if (this.hasInProgress(item)) {
        this.navigateToPlannerSubmission(item, item.currentSubmission);
      } else if (item.selectedDueDate) {
        this.navigateToPlanner(item);
      } else {
        this.startNewPlanner(item);
      }
    },

    startNewPlanner(item) {
      const path = `/planner/${item.plannerId}/${item.id}/0`;
      this.$router.push(path);
    },

    navigateToPlanner(item) {
      let path = `/planner/${item.plannerId}/${item.id}`;
      if (item.selectedDueDate?.id) {
        path += `/${item.selectedDueDate.id}`;
        if (item.selectedDueDate.submissionId) {
          path += `/${item.selectedDueDate.submissionId}`;
        }
      }
      this.$router.push(path);
    },

    navigateToPlannerSubmission(item, submission) {
      this.closeActionMenu();
      const path = `/planner/${item.plannerId}/${item.id}/0/${submission.id}`;
      this.$router.push(path);
    },

    navigateToPlannerWithDate(item, dueDate) {
      this.closeDueDatesDialog();
      let path = `/planner/${item.plannerId}/${item.id}/${dueDate.id}`;
      if (dueDate.submissionId) {
        path += `/${dueDate.submissionId}`;
      }
      this.$router.push(path);
    },

    // === Menu/Dialog Controls ===
    openActionMenu(item, mode) {
      this.actionMenuPlanner = item;
      this.actionMenuMode = mode;
      this.showActionMenuSheet = true;
    },

    closeActionMenu() {
      this.showActionMenuSheet = false;
      this.actionMenuPlanner = null;
    },

    showAllDueDates(item) {
      item.selectDueDateOpen = false;
      this.currentDialogPlanner = item;
      this.showDueDatesDialog = true;
    },

    closeDueDatesDialog() {
      this.showDueDatesDialog = false;
      this.currentDialogPlanner = null;
    },

    selectDueDate(item, dueDate) {
      item.selectedDueDate = dueDate;
      item.selectDueDateOpen = false;
    },

    // === Data Loading ===
    getDueDates(item) {
      const now = DateTime.local();
      const futureDueDates = [];
      const pastDueDates = [];
      const completedDueDates = [];

      item.dueDates.forEach((date) => {
        const dateTime = DateTime.fromISO(date.dueDateTime, { zone: "utc" }).toLocal();
        const processedDate = { ...date, dateTime };

        if (date.status === 2) {
          completedDueDates.push(processedDate);
        } else if (dateTime > now) {
          futureDueDates.push(processedDate);
        } else if ([0, 1, 3].includes(date.status)) {
          pastDueDates.push(processedDate);
        }
      });

      return {
        futureDueDates: futureDueDates.sort((a, b) => a.dateTime.valueOf() - b.dateTime.valueOf()),
        pastDueDates: [
          ...pastDueDates.sort((a, b) => b.dateTime.valueOf() - a.dateTime.valueOf()),
          ...completedDueDates.sort((a, b) => b.dateTime.valueOf() - a.dateTime.valueOf()),
        ],
      };
    },

    getSelectedDueDate(planner, futureDueDates, pastDueDates) {
      let selectedDueDate = null;
      if (planner.currentSubmission?.dueDateId) {
        selectedDueDate = planner.dueDates.find((d) => d.id === planner.currentSubmission.dueDateId);
        if (selectedDueDate) {
          selectedDueDate.completionPercentage = planner.currentSubmission.completionPercentage;
        }
      }
      if (!selectedDueDate) {
        selectedDueDate = futureDueDates[0] || pastDueDates[0] || null;
      }
      return selectedDueDate;
    },

    getDueDatesLimited(item) {
      const allDates = [
        ...item.futureDueDates,
        ...item.pastDueDates.filter((d) => d.status !== 2),
        ...item.pastDueDates.filter((d) => d.status === 2),
      ];
      return allDates.slice(0, this.dueDateLimit);
    },

    getTotalDueDates(item) {
      return item.futureDueDates.length + item.pastDueDates.length;
    },

    async loadPlanners(refresh = false) {
      this.loading = true;
      try {
        const params = {
          "paging.orderbyfield": "id",
          "paging.ascending": false,
          "paging.skipnum": refresh ? 0 : this.skipNum,
          "paging.takenum": this.takeNum,
          contentwildcardsearch: this.search ?? "",
        };

        if (refresh) {
          this.planners = [];
          this.skipNum = 0;
          this.endOfResults = false;
        }

        const r = await PlannerService.getPlannerAssignments(params);
        this.totalItems = r.meta.numTotal;

        const plannerAssignments = r.data?.data || r.data;
        const activePlanners = plannerAssignments.filter((p) => p.status !== 4);
        const processedPlanners = activePlanners.map((planner) => {
          const { futureDueDates, pastDueDates } = this.getDueDates(planner);
          return {
            ...planner,
            futureDueDates,
            pastDueDates,
            selectedDueDate: this.getSelectedDueDate(planner, futureDueDates, pastDueDates),
            selectDueDateOpen: false,
          };
        });

        if (!refresh) {
          this.planners = [...(this.planners || []), ...processedPlanners];
        } else {
          this.planners = processedPlanners;
        }
        this.skipNum = this.planners.length;
        this.endOfResults = r.meta.endOfResults;
      } catch (error) {
        console.error("Error loading planners:", error);
      } finally {
        this.loading = false;
      }
    },

    loadMorePlanners() {
      this.loadPlanners();
    },
  },

  mounted() {
    this.loadPlanners(true);
  },

  watch: {
    search() {
      this.loadPlanners(true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/variables";

.planner-card-wrapper {
  position: relative;
}

.planner-card {
  border-left: 4px solid $color-planners !important;
  border-radius: 4px !important;
  overflow: hidden;
  background: rgba(128, 128, 128, 0.08);

  &.theme--dark {
    background: rgba(255, 255, 255, 0.05);
  }
}

.planner-info {
  padding-bottom: 8px !important;
}

.planner-name {
  line-height: 1.3;
  word-break: break-word;
}

.planner-status-badge {
  flex-shrink: 0;
}

.due-date-btn {
  text-transform: none;
  letter-spacing: normal;
  font-family: inherit !important;
  height: auto !important;
  min-height: 20px !important;
  padding: 2px 4px !important;
}

// Action buttons row
.planner-actions {
  display: flex;
  border-top: 1px solid rgba(0, 0, 0, 0.15);

  .theme--dark & {
    border-top-color: rgba(255, 255, 255, 0.15);
  }
}

.planner-action-btn {
  flex: 1;
  height: 40px !important;
  min-width: 0 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  color: inherit !important;
  opacity: 0.85;
  border-radius: 0 !important;
  background: rgba(0, 0, 0, 0.02) !important;

  .theme--dark & {
    background: rgba(255, 255, 255, 0.02) !important;
  }

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.06) !important;
  }

  .theme--dark &:hover {
    background: rgba(255, 255, 255, 0.06) !important;
  }

  // Divider between buttons
  &:not(:last-child) {
    border-right: 1px solid rgba(0, 0, 0, 0.15);

    .theme--dark & {
      border-right-color: rgba(255, 255, 255, 0.15);
    }
  }
}

// Status badges - all outlined style
.status-badge-block {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  border: 1px solid;
}

// Ready (status 0 or open-ended without assignment)
.status-badge--ready,
.status-badge--warning {
  border-color: #9e9e9e;
  color: #616161;
  background: transparent;

  .theme--dark & {
    border-color: #bdbdbd;
    color: #bdbdbd;
  }
}

// In Progress (status 1)
.status-badge--primary {
  border-color: #2196f3;
  color: #1976d2;
  background: transparent;

  .theme--dark & {
    border-color: #64b5f6;
    color: #64b5f6;
  }
}

// Completed (status 2)
.status-badge--success {
  border-color: #4caf50;
  color: #388e3c;
  background: transparent;

  .theme--dark & {
    border-color: #81c784;
    color: #81c784;
  }
}

// Overdue or Error (status 5 or 4)
.status-badge--error {
  border-color: #f44336;
  color: #d32f2f;
  background: transparent;

  .theme--dark & {
    border-color: #e57373;
    color: #e57373;
  }
}

// Cancelled (status 3)
.status-badge--grey {
  border-color: #9e9e9e;
  color: #616161;
  background: transparent;

  .theme--dark & {
    border-color: #bdbdbd;
    color: #bdbdbd;
  }
}

// Info (submitted, in review, etc.)
.status-badge--info {
  border-color: #00bcd4;
  color: #0097a7;
  background: transparent;

  .theme--dark & {
    border-color: #4dd0e1;
    color: #4dd0e1;
  }
}

// Mobile adjustments
@media (max-width: 600px) {
  .planner-action-btn {
    font-size: 12px !important;
    height: 44px !important;

    .v-icon {
      font-size: 16px !important;
    }
  }
}
</style>
